/**
 * Coups de pouce — Attendu 1085 « Mobiliser une procédure adaptée (linéarité
 * pour la multiplication ou l'addition, retour à l'unité) ».
 *
 * Une entrée par generator_key (cf. gen_1085.js) — chaque carte enseigne UNE
 * procédure de la fiche n°47, l'aide montre le geste sur des valeurs HORS des
 * viviers du gen (revue 2026-09-01) et ne donne jamais la réponse du tirage.
 * SVG pas à pas pilotés par les flèches de la sheet (data-etapes /
 * data-caption, mécanisme 2026-08-21), tableau à flèches canonique du BO.
 */

/*
 * linearite_mult_saisie : 5 personnes → 350 g de pâtes (base 5 et valeur
 * unitaire 70 g hors viviers, « pâtes » hors pool d'ingrédients), cible 10.
 */
const SVG_LINEARITE_MULT = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 216" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="La linéarité multiplicative, en 3 étapes à faire défiler avec les flèches. Pour 5 personnes, il faut 350 grammes de pâtes ; combien pour 10 personnes ? 10, c'est le double de 5 : multiplier par 2. Le même geste sur les quantités : 350 plus 350 égale 700 grammes. Ce que je fais en haut, je le fais en bas">
  <style>
    @keyframes cp1085-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1085-pop { opacity: 0; animation: cp1085-pop 0.5s ease forwards; }
  </style>
  <!-- sous-titres : un par étape (exclusifs, pilotés par la sheet) -->
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">Pour 5 personnes : 350 g de pâtes. Et pour 10 ?</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="0" data-caption="2" font-family="system-ui,sans-serif">10, c'est le double de 5 : × 2</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="3" font-family="system-ui,sans-serif">Le même geste en bas : 350 + 350 = 700 g</text>

  <!-- ── Le tableau (étape 1) ────────────────────────────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    <rect x="80" y="56" width="220" height="52" fill="#ffffff" stroke="#94a3b8" stroke-width="1.4"/>
    <line x1="170" y1="56" x2="170" y2="108" stroke="#94a3b8" stroke-width="1.4"/>
    <line x1="235" y1="56" x2="235" y2="108" stroke="#94a3b8" stroke-width="1.4"/>
    <line x1="80" y1="82" x2="300" y2="82" stroke="#94a3b8" stroke-width="1.4"/>
    <text x="125" y="74" font-size="12.5" fill="#111827" text-anchor="middle">Personnes</text>
    <text x="202.5" y="74" font-size="12.5" font-weight="700" fill="#111827" text-anchor="middle">5</text>
    <text x="267.5" y="74" font-size="12.5" font-weight="700" fill="#111827" text-anchor="middle">10</text>
    <text x="125" y="100" font-size="12.5" fill="#111827" text-anchor="middle">Pâtes</text>
    <text x="202.5" y="100" font-size="12.5" font-weight="700" fill="#111827" text-anchor="middle">350 g</text>
    <text x="267.5" y="100" font-size="14" font-weight="800" fill="#B45309" text-anchor="middle">?</text>
  </g>

  <!-- ── Le regard sur la grandeur connue (étape 2) ──────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1085-pop">
      <path d="M 202.5 52 C 215 40 255 40 267.5 52" fill="none" stroke="#1E3A8A" stroke-width="2.2"/>
      <path d="M 267.5 52 l -8 -2 l 3 -7 Z" fill="#1E3A8A"/>
      <text x="235" y="38" font-size="12" font-weight="800" fill="#1E3A8A" text-anchor="middle">×2</text>
    </g>
  </g>

  <!-- ── Le même geste sur l'autre grandeur (étape 3) ────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1085-pop">
      <path d="M 202.5 112 C 215 124 255 124 267.5 112" fill="none" stroke="#1D9E75" stroke-width="2.2"/>
      <path d="M 267.5 112 l -8 2 l 3 7 Z" fill="#1D9E75"/>
      <text x="235" y="138" font-size="12" font-weight="800" fill="#1D9E75" text-anchor="middle">×2</text>
      <rect x="238" y="87" width="59" height="17" fill="#ffffff"/>
      <text x="267.5" y="100" font-size="12.5" font-weight="800" fill="#1D9E75" text-anchor="middle">700 g</text>
      <text x="8" y="160" font-size="12.5" font-weight="700" fill="#1D9E75">350 + 350 = 700</text>
      <rect x="20" y="172" width="320" height="26" rx="10" fill="#eff6ff" stroke="#bfdbfe"/>
      <text x="180" y="189" font-size="12" font-weight="800" fill="#1E3A8A" text-anchor="middle">Ce que je fais en haut, je le fais en bas.</text>
    </g>
  </g>

  <text x="180" y="213" font-size="11" font-weight="700" fill="#b45309" text-anchor="middle" opacity="0" data-caption="1" font-family="system-ui,sans-serif">↓ Touche les flèches pour avancer, étape par étape ↓</text>
</svg>`.trim();

/*
 * linearite_add_saisie : carottes à 2 € le kilo — le trio 5/2/4 kg et la
 * cible 7 sont introuvables dans le gen (cibles possibles : 6, 9, 12, 15, 18).
 * L'étape 2 matérialise la LIGNE INTRUSE : cohérente, mais inutile.
 */
const SVG_LINEARITE_ADD = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 232" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="La linéarité additive, en 3 étapes à faire défiler avec les flèches. Un tableau : 5 kg de carottes coûtent 10 euros, 2 kg coûtent 4 euros, 4 kg coûtent 8 euros ; combien pour 7 kg ? 7 égale 5 plus 2 : voilà mes deux colonnes, la colonne du 4 ne sert à rien. J'additionne les prix de ces deux colonnes : 10 plus 4 égale 14 euros. Choisir en haut, additionner en bas">
  <style>
    @keyframes cp1085b-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1085b-pop { opacity: 0; animation: cp1085b-pop 0.5s ease forwards; }
  </style>
  <!-- sous-titres : un par étape (exclusifs, pilotés par la sheet) -->
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">5 kg : 10 € ; 2 kg : 4 € ; 4 kg : 8 €. Et pour 7 kg ?</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">7 = 5 + 2 : mes deux colonnes (pas le 4 !)</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="3" font-family="system-ui,sans-serif">J'additionne les prix : 10 + 4 = 14 €</text>

  <!-- ── Le tableau des carottes (étape 1) ───────────────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    <rect x="60" y="56" width="270" height="52" fill="#ffffff" stroke="#94a3b8" stroke-width="1.4"/>
    <line x1="150" y1="56" x2="150" y2="108" stroke="#94a3b8" stroke-width="1.4"/>
    <line x1="210" y1="56" x2="210" y2="108" stroke="#94a3b8" stroke-width="1.4"/>
    <line x1="270" y1="56" x2="270" y2="108" stroke="#94a3b8" stroke-width="1.4"/>
    <line x1="60" y1="82" x2="330" y2="82" stroke="#94a3b8" stroke-width="1.4"/>
    <text x="105" y="74" font-size="12.5" fill="#111827" text-anchor="middle">Kilos</text>
    <text x="180" y="74" font-size="12.5" font-weight="700" fill="#111827" text-anchor="middle">5</text>
    <text x="240" y="74" font-size="12.5" font-weight="700" fill="#111827" text-anchor="middle">2</text>
    <text x="300" y="74" font-size="12.5" font-weight="700" fill="#111827" text-anchor="middle">4</text>
    <text x="105" y="100" font-size="12.5" fill="#111827" text-anchor="middle">Prix</text>
    <text x="180" y="100" font-size="12.5" font-weight="700" fill="#111827" text-anchor="middle">10 €</text>
    <text x="240" y="100" font-size="12.5" font-weight="700" fill="#111827" text-anchor="middle">4 €</text>
    <text x="300" y="100" font-size="12.5" font-weight="700" fill="#111827" text-anchor="middle">8 €</text>
    <text x="8" y="132" font-size="12.5" font-weight="700" fill="#111827">Pour 7 kg :</text>
    <text x="100" y="132" font-size="14" font-weight="800" fill="#B45309">?</text>
  </g>

  <!-- ── Le choix de la paire (étape 2) ──────────────────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1085b-pop">
      <rect x="271" y="57" width="58" height="50" fill="#94a3b8" opacity="0.3"/>
      <ellipse cx="180" cy="69" rx="16" ry="13" fill="none" stroke="#1D9E75" stroke-width="2.2"/>
      <ellipse cx="240" cy="69" rx="16" ry="13" fill="none" stroke="#1D9E75" stroke-width="2.2"/>
      <text x="8" y="156" font-size="12.5" font-weight="700" fill="#1D9E75">5 + 2 = 7 ✓</text>
    </g>
  </g>

  <!-- ── L'addition des prix (étape 3) ───────────────────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1085b-pop">
      <ellipse cx="180" cy="95" rx="22" ry="13" fill="none" stroke="#1D9E75" stroke-width="2.2"/>
      <ellipse cx="240" cy="95" rx="19" ry="13" fill="none" stroke="#1D9E75" stroke-width="2.2"/>
      <text x="8" y="178" font-size="12.5" font-weight="700" fill="#1D9E75">10 + 4 = 14</text>
      <rect x="92" y="119" width="44" height="17" fill="#ffffff"/>
      <text x="100" y="132" font-size="12.5" font-weight="800" fill="#1D9E75">14 €</text>
      <rect x="20" y="188" width="320" height="26" rx="10" fill="#eff6ff" stroke="#bfdbfe"/>
      <text x="180" y="205" font-size="12" font-weight="800" fill="#1E3A8A" text-anchor="middle">Choisir en haut, additionner en bas.</text>
    </g>
  </g>

  <text x="180" y="229" font-size="11" font-weight="700" fill="#b45309" text-anchor="middle" opacity="0" data-caption="1" font-family="system-ui,sans-serif">↓ Touche les flèches pour avancer, étape par étape ↓</text>
</svg>`.trim();

/*
 * retour_unite_saisie : 5 personnes → 400 g de semoule, cible 8 — couple
 * (5, 8), valeur unitaire 80 g et « semoule » tous hors viviers du gen.
 * La colonne « 1 », donnée vide dès l'étape 1, matérialise le passage.
 */
const SVG_RETOUR_UNITE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 236" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Le retour à l'unité, en 3 étapes à faire défiler avec les flèches. Pour 5 personnes, il faut 400 grammes de semoule ; combien pour 8 personnes ? Premier geste, la valeur d'un seul : 400 divisé par 5 égale 80 grammes pour une personne. Deuxième geste : 80 multiplié par 8 égale 640 grammes. La valeur d'un seul d'abord, puis je multiplie">
  <style>
    @keyframes cp1085c-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1085c-pop { opacity: 0; animation: cp1085c-pop 0.5s ease forwards; }
  </style>
  <!-- sous-titres : un par étape (exclusifs, pilotés par la sheet) -->
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">Pour 5 personnes : 400 g de semoule. Et pour 8 ?</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">La valeur d'un seul : 400 ÷ 5 = 80 g</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="3" font-family="system-ui,sans-serif">Puis je multiplie : 80 × 8 = 640 g</text>

  <!-- ── Le tableau avec la colonne « 1 » (étape 1) ──────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    <rect x="60" y="56" width="270" height="52" fill="#ffffff" stroke="#94a3b8" stroke-width="1.4"/>
    <line x1="150" y1="56" x2="150" y2="108" stroke="#94a3b8" stroke-width="1.4"/>
    <line x1="210" y1="56" x2="210" y2="108" stroke="#94a3b8" stroke-width="1.4"/>
    <line x1="270" y1="56" x2="270" y2="108" stroke="#94a3b8" stroke-width="1.4"/>
    <line x1="60" y1="82" x2="330" y2="82" stroke="#94a3b8" stroke-width="1.4"/>
    <text x="105" y="74" font-size="12.5" fill="#111827" text-anchor="middle">Personnes</text>
    <text x="180" y="74" font-size="12.5" font-weight="700" fill="#111827" text-anchor="middle">5</text>
    <text x="240" y="74" font-size="12.5" font-weight="700" fill="#1E3A8A" text-anchor="middle">1</text>
    <text x="300" y="74" font-size="12.5" font-weight="700" fill="#111827" text-anchor="middle">8</text>
    <text x="105" y="100" font-size="12.5" fill="#111827" text-anchor="middle">Semoule</text>
    <text x="180" y="100" font-size="12.5" font-weight="700" fill="#111827" text-anchor="middle">400 g</text>
    <text x="240" y="100" font-size="14" font-weight="800" fill="#B45309" text-anchor="middle">?</text>
    <text x="300" y="100" font-size="14" font-weight="800" fill="#B45309" text-anchor="middle">?</text>
  </g>

  <!-- ── Premier geste : ÷ 5 vers la colonne « 1 » (étape 2) ─────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1085c-pop">
      <path d="M 180 52 C 192 40 228 40 240 52" fill="none" stroke="#1E3A8A" stroke-width="2.2"/>
      <path d="M 240 52 l -8 -2 l 3 -7 Z" fill="#1E3A8A"/>
      <text x="210" y="38" font-size="12" font-weight="800" fill="#1E3A8A" text-anchor="middle">÷5</text>
      <path d="M 180 112 C 192 124 228 124 240 112" fill="none" stroke="#1D9E75" stroke-width="2.2"/>
      <path d="M 240 112 l -8 2 l 3 7 Z" fill="#1D9E75"/>
      <text x="210" y="138" font-size="12" font-weight="800" fill="#1D9E75" text-anchor="middle">÷5</text>
      <rect x="218" y="87" width="44" height="17" fill="#ffffff"/>
      <text x="240" y="100" font-size="12.5" font-weight="800" fill="#1D9E75" text-anchor="middle">80 g</text>
      <text x="8" y="160" font-size="12.5" font-weight="700" fill="#1D9E75">400 ÷ 5 = 80</text>
    </g>
  </g>

  <!-- ── Deuxième geste : × 8 vers la cible (étape 3) ────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1085c-pop">
      <path d="M 240 52 C 252 40 288 40 300 52" fill="none" stroke="#1E3A8A" stroke-width="2.2"/>
      <path d="M 300 52 l -8 -2 l 3 -7 Z" fill="#1E3A8A"/>
      <text x="270" y="38" font-size="12" font-weight="800" fill="#1E3A8A" text-anchor="middle">×8</text>
      <path d="M 240 112 C 252 124 288 124 300 112" fill="none" stroke="#1D9E75" stroke-width="2.2"/>
      <path d="M 300 112 l -8 2 l 3 7 Z" fill="#1D9E75"/>
      <text x="270" y="138" font-size="12" font-weight="800" fill="#1D9E75" text-anchor="middle">×8</text>
      <rect x="274" y="87" width="52" height="17" fill="#ffffff"/>
      <text x="300" y="100" font-size="12.5" font-weight="800" fill="#1D9E75" text-anchor="middle">640 g</text>
      <text x="8" y="182" font-size="12.5" font-weight="700" fill="#1D9E75">80 × 8 = 640</text>
      <rect x="20" y="192" width="320" height="26" rx="10" fill="#eff6ff" stroke="#bfdbfe"/>
      <text x="180" y="209" font-size="12" font-weight="800" fill="#1E3A8A" text-anchor="middle">La valeur d'un seul d'abord, puis je multiplie.</text>
    </g>
  </g>

  <text x="180" y="233" font-size="11" font-weight="700" fill="#b45309" text-anchor="middle" opacity="0" data-caption="1" font-family="system-ui,sans-serif">↓ Touche les flèches pour avancer, étape par étape ↓</text>
</svg>`.trim();

/*
 * procedure_qcm_unique : même situation que le CP n°1 (5 personnes → 350 g de
 * pâtes, hors viviers) — c'est la CIBLE qui commande le geste. L'étape 4
 * barre le piège additif (question g. de la fiche : « 300 + 1 »).
 */
const SVG_CHOIX_GESTE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 242" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="4"
     aria-label="Choisir le bon geste, en 4 étapes à faire défiler avec les flèches. Pour 5 personnes, il faut 350 grammes de pâtes. Pour 10 personnes : 10 égale 5 fois 2, un seul geste, 350 fois 2. Pour 8 personnes : 8 n'est ni un double ni une moitié de 5, je passe par la valeur d'un seul, 350 divisé par 5 puis fois 8. Et 350 plus 3 ? Non : 3 personnes de plus, ce n'est pas 3 grammes de plus. Le bon geste dépend du nombre visé">
  <style>
    @keyframes cp1085d-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1085d-pop { opacity: 0; animation: cp1085d-pop 0.5s ease forwards; }
  </style>
  <!-- sous-titres : un par étape (exclusifs, pilotés par la sheet) -->
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">Pour 5 personnes : 350 g de pâtes</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">Pour 10 ? 10 = 5 × 2 : un seul geste</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="3" font-family="system-ui,sans-serif">Pour 8 ? Ni double, ni moitié : la valeur d'un seul</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="4" font-family="system-ui,sans-serif">350 + 3 ? Non : 3 personnes ≠ 3 grammes</text>

  <!-- ── La situation (étape 1) ──────────────────────────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    <rect x="40" y="36" width="280" height="28" rx="8" fill="#f8fafc" stroke="#94a3b8"/>
    <text x="180" y="55" font-size="12.5" font-weight="700" fill="#111827" text-anchor="middle">Pour 5 personnes : 350 g de pâtes</text>
  </g>

  <!-- ── Cible 10 : un seul geste (étape 2) ──────────────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1085d-pop">
      <text x="8" y="96" font-size="12.5" font-weight="700" fill="#111827">Pour 10 :</text>
      <text x="88" y="96" font-size="12.5" font-weight="700" fill="#1E3A8A">10 = 5 × 2</text>
      <rect x="250" y="80" width="100" height="24" rx="8" fill="#ecfdf5" stroke="#1D9E75"/>
      <text x="300" y="96" font-size="12.5" font-weight="800" fill="#1D9E75" text-anchor="middle">350 × 2</text>
    </g>
  </g>

  <!-- ── Cible 8 : la valeur d'un seul (étape 3) ─────────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1085d-pop">
      <text x="8" y="136" font-size="12.5" font-weight="700" fill="#111827">Pour 8 :</text>
      <text x="88" y="136" font-size="12.5" font-weight="700" fill="#1E3A8A">ni double, ni moitié</text>
      <rect x="228" y="120" width="124" height="24" rx="8" fill="#ecfdf5" stroke="#1D9E75"/>
      <text x="290" y="136" font-size="12.5" font-weight="800" fill="#1D9E75" text-anchor="middle">350 ÷ 5 × 8</text>
    </g>
  </g>

  <!-- ── Le piège additif, barré (étape 4) ───────────────────────────── -->
  <g data-etape="4" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1085d-pop">
      <rect x="8" y="162" width="96" height="24" rx="8" fill="#fffbeb" stroke="#B45309"/>
      <text x="56" y="178" font-size="12.5" font-weight="800" fill="#B45309" text-anchor="middle">350 + 3</text>
      <line x1="14" y1="174" x2="98" y2="174" stroke="#B45309" stroke-width="2.2"/>
      <text x="116" y="178" font-size="12" font-weight="700" fill="#B45309">3 personnes de plus ≠ 3 g de plus ✗</text>
      <rect x="20" y="198" width="320" height="26" rx="10" fill="#eff6ff" stroke="#bfdbfe"/>
      <text x="180" y="215" font-size="12" font-weight="800" fill="#1E3A8A" text-anchor="middle">Le bon geste dépend du nombre visé.</text>
    </g>
  </g>

  <text x="180" y="239" font-size="11" font-weight="700" fill="#b45309" text-anchor="middle" opacity="0" data-caption="1" font-family="system-ui,sans-serif">↓ Touche les flèches pour avancer, étape par étape ↓</text>
</svg>`.trim();

const CP_1085 = {
    linearite_mult_saisie: {
        pages: [
            {
                titre: 'Le même geste des deux côtés',
                lignes: [
                    '**Compare les deux nombres de la même grandeur** : le nombre visé est-il le double, le triple ou la moitié du nombre donné ?',
                    '**Ce que tu fais d\'un côté, tu le fais de l\'autre** : si le nombre de personnes double, la quantité double aussi.',
                    'La moitié : je divise par 2.',
                ],
            },
            { titre: 'Un exemple : 5 personnes, puis 10', svg: SVG_LINEARITE_MULT },
        ],
    },
    linearite_add_saisie: {
        pages: [
            {
                titre: 'Assembler deux lignes',
                lignes: [
                    '**La quantité visée se fabrique avec celles qu\'on te donne** : cherche deux lignes qui, ensemble, font le nombre visé (7, c\'est 5 + 2).',
                    'Alors les quantités s\'ajoutent aussi : la valeur pour 5 plus la valeur pour 2, c\'est la valeur pour 7.',
                    '**Une des lignes ne sert à rien** : choisis tes deux lignes sur les personnes (ou les kilos), jamais au hasard.',
                ],
            },
            { titre: 'Un exemple : 7 kg de carottes', svg: SVG_LINEARITE_ADD },
        ],
    },
    retour_unite_saisie: {
        pages: [
            {
                titre: 'Les deux gestes',
                lignes: [
                    '**Premier geste : la valeur d\'un seul.** Un kilo, une personne, un objet : je divise la quantité donnée.',
                    '**Deuxième geste : je multiplie** cette valeur d\'un seul par le nombre visé.',
                    'Ce chemin marche **toujours**, même quand le nombre visé n\'est ni un double, ni un triple, ni une moitié.',
                ],
            },
            { titre: 'Un exemple : la colonne du 1', svg: SVG_RETOUR_UNITE },
        ],
    },
    procedure_qcm_unique: {
        pages: [
            {
                titre: 'Quel geste choisir ?',
                lignes: [
                    '**Regarde le nombre visé avant de calculer** : est-il le double, le triple, le quadruple ou la moitié du nombre donné ? Alors un seul geste suffit : multiplier ou diviser.',
                    'Sinon : je passe par la valeur d\'un seul (je divise, puis je multiplie).',
                    '**On ne mélange pas les grandeurs** : 3 personnes de plus, ce n\'est jamais 3 grammes de plus.',
                ],
            },
            { titre: 'Un exemple : une situation, trois cibles', svg: SVG_CHOIX_GESTE },
        ],
    },
};

export default CP_1085;
